/**
 * Minimal API client for LexiPro Forensic OS.
 * - Uses same-origin /api by default (nginx + dev proxy)
 * - Sends cookies for auth and workspace scoping when present
 */
import { getAuthToken, getWorkspaceId } from "./authStorage";
import { CASE_CONFIG } from "./CaseConfig";
import { getCsrfHeader } from "./csrf";
import { getApiBase } from "./apiBase";

type InternalInit = RequestInit & { _retry?: boolean };

function sanitizeErrorMessage(raw: string, fallback: string) {
  const text = (raw || "").trim();
  if (!text) return fallback;
  const singleLine = text.split(/\r?\n/)[0].replace(/^Error:\s*/i, "").trim();
  const stripped = singleLine.replace(/<[^>]*>/g, "").trim();
  if (!stripped) return fallback;
  return stripped.length > 240 ? `${stripped.slice(0, 237)}...` : stripped;
}

async function readErrorMessage(res: Response) {
  const fallback = `${res.status} ${res.statusText}`.trim();
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      const data = await res.json();
      const message = data?.message || data?.error || data?.errorMessage || "";
      return sanitizeErrorMessage(String(message), fallback);
    } catch {
      return fallback;
    }
  }
  const text = await res.text().catch(() => "");
  return sanitizeErrorMessage(text, fallback);
}

function authHeaders(): Record<string, string> {
  const workspaceId = getWorkspaceId();
  const headers: Record<string, string> = {};
  if (workspaceId) headers["x-workspace-id"] = workspaceId;
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function buildAuthHeaders(token?: string, workspaceIdOverride?: string): Record<string, string> {
  const headers = { ...authHeaders() };
  if (workspaceIdOverride) headers["x-workspace-id"] = workspaceIdOverride;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function injectCsrfHeader(headers: Record<string, string>) {
  if (headers["x-csrf-token"]) return;
  if (typeof document !== "undefined") {
    const match = document.cookie.match(/(?:^|; )forensic_csrf=([^;]+)/);
    if (match?.[1]) {
      headers["x-csrf-token"] = match[1];
      return;
    }
  }
  const csrf = getCsrfHeader();
  if (csrf["x-csrf-token"]) headers["x-csrf-token"] = csrf["x-csrf-token"];
}

async function request(method: string, path: string, body?: any, init?: InternalInit) {
  const base = getApiBase().replace(/\/$/, "");
  const url = path.startsWith("http") ? path : `${base}${path.startsWith("/") ? "" : "/"}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...authHeaders(),
  };
  if (!["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase())) {
    Object.assign(headers, getCsrfHeader());
    injectCsrfHeader(headers);
  }
  Object.assign(headers, init?.headers as any);

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
    // browser sets boundary; don't set Content-Type
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: payload,
      credentials: "include",
      ...init,
    });
  } catch (err) {
    const shouldFallback = typeof window !== "undefined" && /^https?:\/\//i.test(url);
    if (!shouldFallback) throw err;
    const fallbackBase = "/api";
    const fallbackUrl = path.startsWith("http")
      ? path
      : `${fallbackBase}${path.startsWith("/") ? "" : "/"}${path}`;
    res = await fetch(fallbackUrl, {
      method,
      headers,
      body: payload,
      credentials: "include",
      ...init,
    });
  }

  if (res.status === 401 && typeof window !== "undefined") {
    if (!init?._retry) {
      const retryHeaders: Record<string, string> = {
        Accept: "application/json",
        ...authHeaders(),
        ...(init?.headers as any),
      };
      if (!["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase())) {
        Object.assign(retryHeaders, getCsrfHeader());
        injectCsrfHeader(retryHeaders);
      } else {
        injectCsrfHeader(retryHeaders);
      }
      return request(method, path, body, { ...init, headers: retryHeaders, _retry: true });
    }
  }
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return res.json();
  return res.text();
}

export const api = {
  get: (path: string, init?: InternalInit) => request("GET", path, undefined, init),
  post: (path: string, body?: any, init?: InternalInit) => request("POST", path, body, init),
  put: (path: string, body?: any, init?: InternalInit) => request("PUT", path, body, init),
  del: (path: string, init?: InternalInit) => request("DELETE", path, undefined, init),

  postForm: (path: string, form: FormData) => request("POST", path, form),

  // Downloads a PDF (or any blob) and returns an object URL
  downloadBlobUrl: async (path: string, body?: any) => {
    const base = getApiBase().replace(/\/$/, "");
    const url = path.startsWith("http") ? path : `${base}${path.startsWith("/") ? "" : "/"}${path}`;

    const headers: Record<string, string> = {
      ...authHeaders(),
      ...(body ? { "Content-Type": "application/json" } : {}),
    };
    if (body) {
      Object.assign(headers, getCsrfHeader());
      injectCsrfHeader(headers);
    }

    let res: Response;
    try {
      res = await fetch(url, {
        method: body ? "POST" : "GET",
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
      });
    } catch (err) {
      const shouldFallback = typeof window !== "undefined" && /^https?:\/\//i.test(url);
      if (!shouldFallback) throw err;
      const fallbackUrl = path.startsWith("http") ? path : `/api${path.startsWith("/") ? "" : "/"}${path}`;
      res = await fetch(fallbackUrl, {
        method: body ? "POST" : "GET",
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
      });
    }

    if (!res.ok) {
      throw new Error(await readErrorMessage(res));
    }

    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
  fetchExhibitFile: async (exhibitId: string, token: string, matterId?: string) =>
    fetchExhibitFile(exhibitId, token, matterId),
  chatWithAI: async (message: string, workspaceId: string, token: string, matterId?: string) =>
    chatWithAI(message, workspaceId, token, matterId),
};

export const fetchExhibitFile = async (
  exhibitId: string,
  token: string,
  workspaceId: string = CASE_CONFIG.DEMO_WORKSPACE_ID,
  matterId: string = CASE_CONFIG.DEMO_MATTER_ID
): Promise<Blob> => {
  const base = getApiBase().replace(/\/$/, "");
  const url = `${base}/workspaces/${workspaceId}/exhibits/${exhibitId}/file?matterId=${encodeURIComponent(matterId)}`;

  let res: Response;
  try {
    res = await fetch(url, {
      headers: {
        ...buildAuthHeaders(token, workspaceId)
      },
      credentials: "include",
    });
  } catch (err) {
    const fallbackUrl = `/api/workspaces/${workspaceId}/exhibits/${exhibitId}/file?matterId=${encodeURIComponent(matterId)}`;
    res = await fetch(fallbackUrl, {
      headers: {
        ...buildAuthHeaders(token, workspaceId)
      },
      credentials: "include",
    });
  }

  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  return res.blob();
};

export const chatWithAI = async (
  message: string,
  workspaceId: string = CASE_CONFIG.DEMO_WORKSPACE_ID,
  token: string,
  matterId: string = CASE_CONFIG.DEMO_MATTER_ID
) => {
  const base = getApiBase().replace(/\/$/, "");
  let res: Response;
  try {
    res = await fetch(`${base}/ai/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfHeader(),
        ...buildAuthHeaders(token, workspaceId)
      },
      body: JSON.stringify({
        userPrompt: message,
        promptKey: "case_assistant",
        workspaceId,
        matterId
      }),
      credentials: "include",
    });
  } catch (err) {
    res = await fetch(`/api/ai/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfHeader(),
        ...buildAuthHeaders(token, workspaceId)
      },
      body: JSON.stringify({
        userPrompt: message,
        promptKey: "case_assistant",
        workspaceId,
        matterId
      }),
      credentials: "include",
    });
  }

  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return res.json();
  return res.text();
};
